import {
  getLayoutStructureChildLists,
  tableSlotNodes,
} from '../../components/oasis/bespoke/layout/layoutStructureChildLists';
import {
  isPresentationProp,
  isStructuralChildKey,
} from '../../components/oasis/bespoke/layout/layoutNodePropKinds';
import {
  isContainerNodeType,
  isFieldNodeType,
} from '../../components/oasis/bespoke/layout/layoutNodeKinds';
import type {
  ConditionalWhenClause,
  LayoutNode,
} from '../../components/oasis/bespoke/layout/types';
import { resolveConditionalClauses } from '../../components/oasis/bespoke/layout/whenClause';
import { getLayoutCompileError } from '../../components/oasis/bespoke/layout/tryParseLayoutJson';

/**
 * Cross-form design consistency for bespoke layouts.
 *
 * The same question appears on many forms, and its design drifts: 1,175 of the
 * 1,431 questions rendered on more than one form are styled differently
 * somewhere. This module finds those disagreements by comparing templates
 * against each other — it needs no question schema, no `_used_by`, and no
 * RogerDoc call.
 *
 * Three independent passes, because they answer different questions:
 *
 * - **Field treatment** — is one question given the same props everywhere?
 *   Keyed by qid, so it localises to a single question and never produces a
 *   false positive from surrounding structure.
 * - **Container shape** — is a group of questions arranged the same way
 *   everywhere? Keyed by the *set of questions inside the container*, so the
 *   unit of comparison comes from where the layouts already agree about what
 *   belongs together, rather than from a schema order they have drifted from.
 * - **Field gate** — is one question revealed on the same condition
 *   everywhere? Keyed by qid, like field treatment. Only forms that gate the
 *   question take part: a form rendering it ungated is not a *different* way
 *   of gating it, and whether that is a defect depends on the form's own
 *   schema, which this module deliberately does not read. The validator's
 *   `ungated-subitem` issue owns that case, and owns it per-form rather than
 *   by comparison, so it catches the highest-ranked form too.
 *
 * All three are linear in total nodes: a walk, then a group-by. There is no
 * pairwise comparison to optimise.
 */

/** Deterministic JSON with object keys sorted at every depth. */
function canonical(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonical).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/**
 * A node's design, excluding its children and its identity.
 *
 * `qid` is dropped so two placements of the same question compare on styling
 * alone; structural keys are dropped so a container compares on its own shape
 * rather than on what happens to be inside it.
 */
/** Stands in for a node-valued header slot, whose subtree is not this table's design. */
const NODE_SLOT_SIGNATURE = '<nodes>';

export function ownDesignProps(node: LayoutNode): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node)) {
    if (key === 'qid' || isStructuralChildKey(key)) continue;
    if (isPresentationProp(key)) continue;
    props[key] = value;
  }

  // Header text is design -- two tables with different column titles really do
  // differ. A *node* header is not: signing its subtree would drag the classes
  // and qids inside it into the table's own shape and report two identically
  // arranged tables as drifting. Keep the slot (column count still counts) and
  // drop the subtree.
  if (props.type === 'table') {
    if (Array.isArray(props.rowHeader)) props.rowHeader = NODE_SLOT_SIGNATURE;
    if (Array.isArray(props.colHeaders)) {
      props.colHeaders = (props.colHeaders as unknown[]).map(header =>
        Array.isArray(header) ? NODE_SLOT_SIGNATURE : header
      );
    }
  }

  // A `radio-option` is a radio rendered as a table cell -- the same control,
  // arranged differently. Its `value` names one option rather than describing
  // the question, and every option of a group shares the qid, so comparing it
  // would pit a question against itself.
  if (props.type === 'radio-option') {
    props.type = 'OASISRadio';
    delete props.value;
  }
  return props;
}

export interface FieldTreatment {
  qid: string;
  /** Canonical signature of the field's own props. Equal signatures agree. */
  signature: string;
  props: Record<string, unknown>;
}

export interface ContainerArrangement {
  /** Sorted qids inside the container; its identity across forms. */
  qids: string[];
  /** Join of {@link qids}, usable as a map key. */
  contentsKey: string;
  signature: string;
  shape: Record<string, unknown>;
  /** Nesting depth, used to keep the innermost container per contents. */
  depth: number;
}

/**
 * The options a compound `OASISMultiselect` renders, canonically.
 *
 * `children` is dropped as structural and `OASISMultiselectOption` is neither
 * a field nor a container, so a compound layout and the plain design-system
 * control fingerprint identically today -- as do two compound layouts whose
 * option labels differ, though a clinician reads them. `undefined` means the
 * plain control: absent and empty are different answers here.
 */
function multiselectOptionLabels(node: LayoutNode): string[] | undefined {
  if (node.type !== 'OASISMultiselect') return undefined;
  const children = (node as { children?: LayoutNode[] }).children;
  if (!children) return undefined;
  const out: string[] = [];
  const visit = (arr: LayoutNode[]) => {
    for (const child of arr) {
      if (child.type === 'OASISMultiselectOption') {
        const option = child as {
          value?: string;
          valueFrom?: string;
          labelOverride?: string;
        };
        out.push(
          canonical({
            value: option.value,
            valueFrom: option.valueFrom,
            labelOverride: option.labelOverride,
          })
        );
      }
      for (const list of getLayoutStructureChildLists(child)) visit(list);
    }
  };
  visit(children);
  return out.sort((a, b) => a.localeCompare(b));
}

/**
 * Every question's own design, in one layout.
 *
 * A question rendered in more than one place (both branches of a
 * `conditional`, say) keeps its first occurrence, matching how the layout's
 * other collectors treat repeats.
 */
export function collectFieldTreatments(
  nodes: LayoutNode[]
): Map<string, FieldTreatment> {
  const treatments = new Map<string, FieldTreatment>();

  const walk = (arr: LayoutNode[]) => {
    for (const node of arr) {
      if (isFieldNodeType(node.type)) {
        const { qid } = node as { qid: string };
        if (!treatments.has(qid)) {
          const props = ownDesignProps(node);
          const optionLabels = multiselectOptionLabels(node);
          if (optionLabels !== undefined) props.optionLabels = optionLabels;
          treatments.set(qid, { qid, signature: canonical(props), props });
        }
      }
      for (const list of getLayoutStructureChildLists(node)) walk(list);
    }
  };

  walk(nodes);
  return treatments;
}

/** Which grouping a question sits in, for one layout. */
export interface FieldGrouping {
  qid: string;
  /** The grouping's node type, or `(none)` when the question stands alone. */
  signature: string;
  detail: Record<string, unknown>;
}

/**
 * The grouping each question sits in, keyed by the question.
 *
 * This is the container pass turned inside out, and it exists because that
 * pass cannot see a grouping that was taken apart. Containers are keyed there
 * by the set of questions they hold, and one holding fewer than two questions
 * is skipped -- so a form that breaks a six-question table into six labelled
 * fields emits no container entry at all, and there is nothing left to compare
 * the table against. Dissolving a group was invisible while merely rearranging
 * one was caught, which is backwards: taking it apart is the bigger change.
 *
 * Keyed per question, `(none)` is a value like any other, so the form that
 * scattered the group disagrees with the forms that kept it.
 *
 * The grouping recorded is the innermost container holding at least two
 * questions -- the same "actual grouping decision" the container pass looks
 * for. A field's own label wrapper holds one question and is not a grouping.
 */
export function collectFieldGroupings(
  nodes: LayoutNode[]
): Map<string, FieldGrouping> {
  const groupings = new Map<string, FieldGrouping>();

  // Distinct qids, not occurrences: one question laid out as a row of
  // `radio-option` cells appears once per cell, and a container holding only
  // that question groups it with nothing.
  const walk = (arr: LayoutNode[]): Set<string> => {
    const found = new Set<string>();
    for (const node of arr) {
      const own = new Set<string>();
      if (isFieldNodeType(node.type)) {
        own.add((node as { qid: string }).qid);
      }
      for (const list of getLayoutStructureChildLists(node)) {
        for (const qid of walk(list)) own.add(qid);
      }

      // Assigned on the way out, so the innermost qualifying container wins:
      // anything already recorded sits in a tighter grouping than this one.
      if (isContainerNodeType(node.type) && own.size >= 2) {
        const groupQids = [...own].sort((a, b) => a.localeCompare(b));
        for (const qid of own) {
          if (!groupings.has(qid)) {
            groupings.set(qid, {
              qid,
              signature: node.type,
              // The company kept, carried for the comparison rather than the
              // key: a form that has none of these cannot group this question
              // however it is laid out.
              detail: { grouping: node.type, groupQids },
            });
          }
        }
      }

      for (const qid of own) found.add(qid);
    }
    return found;
  };

  for (const qid of walk(nodes)) {
    if (!groupings.has(qid)) {
      groupings.set(qid, {
        qid,
        signature: NO_GROUPING,
        detail: { grouping: NO_GROUPING },
      });
    }
  }
  return groupings;
}

/** A question that sits in no grouping at all. */
export const NO_GROUPING = '(none)';

/**
 * Every container's shape, keyed by the questions it holds.
 *
 * Containers are enumerated at every level — no boundary heuristic — and then
 * collapsed to the **innermost** container per contents. Without that collapse
 * a layout like `card[grid[A,B]]` would report two shapes for `{A,B}` and look
 * self-inconsistent; the outer card holds those questions only incidentally,
 * while the grid is the actual grouping decision.
 *
 * Containers holding fewer than two questions are skipped: a wrapper around a
 * single field is field treatment, which the other pass already covers.
 */
export function collectContainerArrangements(
  nodes: LayoutNode[]
): Map<string, ContainerArrangement> {
  const deepest = new Map<string, ContainerArrangement>();

  const walk = (arr: LayoutNode[], depth: number): Set<string> => {
    const found = new Set<string>();
    for (const node of arr) {
      const own = new Set<string>();
      if (isFieldNodeType(node.type)) {
        own.add((node as { qid: string }).qid);
      }
      for (const list of getLayoutStructureChildLists(node)) {
        for (const qid of walk(list, depth + 1)) own.add(qid);
      }

      if (isContainerNodeType(node.type) && own.size >= 2) {
        const qids = [...own].sort((a, b) => a.localeCompare(b));
        const contentsKey = qids.join('|');
        const previous = deepest.get(contentsKey);
        if (!previous || depth > previous.depth) {
          const shape = ownDesignProps(node);
          deepest.set(contentsKey, {
            qids,
            contentsKey,
            signature: canonical(shape),
            shape,
            depth,
          });
        }
      }

      for (const qid of own) found.add(qid);
    }
    return found;
  };

  walk(nodes, 0);
  return deepest;
}

export interface FieldGate {
  qid: string;
  signature: string;
  /**
   * QIDs the gate clauses reference.
   *
   * Kept alongside the rendered clause strings so a caller can ask whether a
   * form is *able* to express the canonical gate: a question re-issued under a
   * new id (M0150 as `m_carerequired_m0150`) forces every form holding the new
   * id to gate on it, and comparing the strings alone reads that as a
   * disagreement no layout can settle.
   */
  qids: string[];
  /**
   * Every gate in scope, described and sorted.
   *
   * A set rather than a chain: two forms that reveal a question on the same
   * conditions agree about it, even when one nests its `conditional`s in the
   * other order or splits a `whenAll` across two nodes.
   */
  gates: string[];
}

/**
 * One gate clause, as authors read it.
 *
 * Values inside `in` are sorted so list order alone never reads as drift;
 * clause shapes with no schema meaning (`derived` totals, `repeatItemEq`)
 * fall back to their canonical JSON, which is still comparable.
 */
function describeClause(
  gateType: 'conditional' | 'disabled-when',
  clause: ConditionalWhenClause,
  negated: boolean
): string {
  const prefix = negated ? `${gateType} not` : gateType;
  if ('qid' in clause) {
    if ('eq' in clause) return `${prefix} ${clause.qid} = ${clause.eq}`;
    if ('includes' in clause)
      return `${prefix} ${clause.qid} includes ${clause.includes}`;
    if ('in' in clause) {
      // Quoted so the join cannot be ambiguous: unquoted, `["A, B", "C"]`
      // and `["A", "B, C"]` both read as `A, B, C` and compare equal.
      const values = [...clause.in]
        .sort((a, b) => a.localeCompare(b))
        .map(value => JSON.stringify(value));
      return `${prefix} ${clause.qid} in [${values.join(', ')}]`;
    }
  }
  return `${prefix} ${canonical(clause)}`;
}

function gateFrame(
  gateType: 'conditional' | 'disabled-when',
  clauses: ConditionalWhenClause[],
  negated: boolean
): string[] {
  return clauses.map(clause => describeClause(gateType, clause, negated));
}

/** QIDs a set of clauses reads, ignoring derived-value clauses. */
function clauseQids(clauses: ConditionalWhenClause[]): string[] {
  return clauses.flatMap(clause =>
    'qid' in clause && typeof clause.qid === 'string' ? [clause.qid] : []
  );
}

/**
 * Every gated question's reveal condition, in one layout.
 *
 * Ungated questions are absent rather than recorded as an empty gate — see
 * the module docstring for why this pass stays silent about them.
 */
export function collectFieldGates(nodes: LayoutNode[]): Map<string, FieldGate> {
  const gates = new Map<string, FieldGate>();

  const walk = (arr: LayoutNode[], scope: string[], scopeQids: string[]) => {
    for (const node of arr) {
      if (isFieldNodeType(node.type) && scope.length > 0) {
        const { qid } = node as { qid: string };
        if (!gates.has(qid)) {
          const sorted = [...scope].sort((a, b) => a.localeCompare(b));
          gates.set(qid, {
            qid,
            signature: canonical(sorted),
            gates: sorted,
            qids: [...new Set(scopeQids)].sort((a, b) => a.localeCompare(b)),
          });
        }
      }

      switch (node.type) {
        case 'conditional': {
          const clauses = resolveConditionalClauses(node);
          const qids = [...scopeQids, ...clauseQids(clauses)];
          walk(
            node.children,
            [...scope, ...gateFrame('conditional', clauses, false)],
            qids
          );
          if (node.elseChildren) {
            // The else branch renders on the negation, which is a different
            // condition even though it is the same clause.
            walk(
              node.elseChildren,
              [...scope, ...gateFrame('conditional', clauses, true)],
              qids
            );
          }
          break;
        }
        case 'disabled-when':
          walk(
            node.children,
            [...scope, ...gateFrame('disabled-when', node.whenAll, false)],
            [...scopeQids, ...clauseQids(node.whenAll)]
          );
          break;
        case 'table':
          walk(tableSlotNodes(node.rowHeader), scope, scopeQids);
          for (const header of node.colHeaders ?? []) {
            walk(tableSlotNodes(header), scope, scopeQids);
          }
          for (const row of node.rows) {
            const clauses = resolveConditionalClauses(row);
            const rowScope = [
              ...scope,
              ...gateFrame('conditional', clauses, false),
            ];
            const rowScopeQids = [...scopeQids, ...clauseQids(clauses)];
            walk(tableSlotNodes(row.label), rowScope, rowScopeQids);
            walk(row.cells, rowScope, rowScopeQids);
          }
          break;
        default:
          for (const list of getLayoutStructureChildLists(node))
            walk(list, scope, scopeQids);
      }
    }
  };

  walk(nodes, [], []);
  return gates;
}

/** One way some forms render a question, or arrange a group. */
export interface Variant<TDetail> {
  signature: string;
  detail: TDetail;
  /** Form types rendering it this way, sorted. */
  forms: string[];
}

export interface ConsistencyIndex {
  /** qid -> the distinct treatments across forms. */
  fields: Map<string, Variant<Record<string, unknown>>[]>;
  /** container contents key -> the distinct arrangements across forms. */
  containers: Map<string, Variant<Record<string, unknown>>[]>;
  /** qid -> the distinct reveal conditions across the forms that gate it. */
  gates: Map<string, Variant<Record<string, unknown>>[]>;
  /** qid -> the distinct groupings it sits in across forms. */
  groupings: Map<string, Variant<Record<string, unknown>>[]>;
}

/** The template fields this module needs; `BespokeTemplateResponse` satisfies it. */
export interface ComparableTemplate {
  form_type: string;
  section_name: string;
  schema_version_name: string;
  form_schema_version: string;
  layout_json?: unknown | null;
}

/**
 * Which generation of forms a template belongs to.
 *
 * Comparisons must stay inside a generation. Schema version names are the form
 * type plus a suffix -- `SOC E2 Enhanced`, `SOCPT E2 Enhanced`, or bare `SOC`
 * -- and the suffix is what different form types have in common. Comparing
 * across generations is apples to oranges: every `E2 Enhanced` template labels
 * a question `(A1010) Race` while every bare one says `Race`, and neither is
 * wrong.
 *
 * Stripping the form type leaves the generation, which is then paired with the
 * schema version so `v0` and a future `v1` never mix.
 */
export function schemaGeneration(template: ComparableTemplate): string {
  const suffix = template.schema_version_name.startsWith(template.form_type)
    ? template.schema_version_name.slice(template.form_type.length).trim()
    : template.schema_version_name.trim();
  return `${suffix || '(base)'} ${template.form_schema_version}`;
}

function addVariant(
  into: Map<string, Map<string, Variant<Record<string, unknown>>>>,
  key: string,
  signature: string,
  detail: Record<string, unknown>,
  form: string
): void {
  let variants = into.get(key);
  if (!variants) {
    variants = new Map();
    into.set(key, variants);
  }
  const existing = variants.get(signature);
  if (existing) {
    if (!existing.forms.includes(form)) existing.forms.push(form);
    return;
  }
  variants.set(signature, { signature, detail, forms: [form] });
}

function finalize(
  built: Map<string, Map<string, Variant<Record<string, unknown>>>>
): Map<string, Variant<Record<string, unknown>>[]> {
  const out = new Map<string, Variant<Record<string, unknown>>[]>();
  for (const [key, variants] of built) {
    const list = [...variants.values()];
    for (const variant of list)
      variant.forms.sort((a, b) => a.localeCompare(b));
    // Most-used first, so the dominant treatment reads as the baseline.
    list.sort((a, b) => b.forms.length - a.forms.length);
    out.set(key, list);
  }
  return out;
}

/**
 * Compare templates against each other, one index per schema generation.
 *
 * Callers decide which templates to include — usually the live ones — since
 * comparing against paused or standby rows would report drift nobody can see.
 */
export function buildConsistencyIndexes(
  templates: ComparableTemplate[]
): Map<string, ConsistencyIndex> {
  const byGeneration = new Map<string, ComparableTemplate[]>();
  for (const template of templates) {
    const generation = schemaGeneration(template);
    const group = byGeneration.get(generation);
    if (group) group.push(template);
    else byGeneration.set(generation, [template]);
  }

  const indexes = new Map<string, ConsistencyIndex>();
  for (const [generation, group] of byGeneration) {
    indexes.set(generation, buildConsistencyIndex(group));
  }
  return indexes;
}

/** Compare one set of templates, which the caller has already scoped. */
export function buildConsistencyIndex(
  templates: ComparableTemplate[]
): ConsistencyIndex {
  const fields = new Map<
    string,
    Map<string, Variant<Record<string, unknown>>>
  >();
  const containers = new Map<
    string,
    Map<string, Variant<Record<string, unknown>>>
  >();
  const gates = new Map<
    string,
    Map<string, Variant<Record<string, unknown>>>
  >();
  const groupings = new Map<
    string,
    Map<string, Variant<Record<string, unknown>>>
  >();

  for (const template of templates) {
    const layout = template.layout_json;
    if (!Array.isArray(layout)) continue;
    // A stored layout is not guaranteed to be walkable by this build: another
    // branch (or a rolled-back deploy) can save a node type this code does not
    // know, and the walkers throw on one. Skip it rather than let the throw
    // escape -- the index is built in a render-phase useMemo with no error
    // boundary above it, so a single foreign node would blank the page. A
    // layout we cannot read should not contribute a canonical treatment
    // either, so skipping is also the right answer for the comparison.
    if (getLayoutCompileError(layout)) continue;
    const nodes = layout as LayoutNode[];

    // The compile gate walks its own code path, not these collectors', so
    // passing it is not a promise that they can read the layout. Run all four
    // before recording anything: a template that throws halfway through would
    // otherwise contribute a partial set of treatments, which reads as a real
    // divergence from the forms that were walked whole.
    let collected;
    try {
      collected = {
        treatments: collectFieldTreatments(nodes),
        arrangements: collectContainerArrangements(nodes),
        fieldGates: collectFieldGates(nodes),
        fieldGroupings: collectFieldGroupings(nodes),
      };
    } catch {
      continue;
    }

    for (const treatment of collected.treatments.values()) {
      addVariant(
        fields,
        treatment.qid,
        treatment.signature,
        treatment.props,
        template.form_type
      );
    }
    for (const arrangement of collected.arrangements.values()) {
      addVariant(
        containers,
        arrangement.contentsKey,
        arrangement.signature,
        arrangement.shape,
        template.form_type
      );
    }
    for (const gate of collected.fieldGates.values()) {
      addVariant(
        gates,
        gate.qid,
        gate.signature,
        { gates: gate.gates, gateQids: gate.qids },
        template.form_type
      );
    }
    for (const grouping of collected.fieldGroupings.values()) {
      addVariant(
        groupings,
        grouping.qid,
        grouping.signature,
        grouping.detail,
        template.form_type
      );
    }
  }

  return {
    fields: finalize(fields),
    containers: finalize(containers),
    gates: finalize(gates),
    groupings: finalize(groupings),
  };
}

/** Keys whose variants disagree, i.e. more than one distinct signature. */
export function divergentKeys(
  index: Map<string, Variant<Record<string, unknown>>[]>
): string[] {
  const keys: string[] = [];
  for (const [key, variants] of index) {
    if (variants.length > 1) keys.push(key);
  }
  return keys.sort((a, b) => a.localeCompare(b));
}
